<script setup lang="ts">
import {computed, onMounted, onUnmounted, ref, watch, withDefaults} from "vue";
import {useI18n} from 'vue-i18n';

const props = withDefaults(
    defineProps<{
      filter: any;
      activeValue: string;
      activePriceSort?: string;
      catalogItemType?: string;
    }>(),
    {
      filter: () => ({}),
      activeValue: "",
      activePriceSort: "",
      catalogItemType: ''
    }
);

const emit = defineEmits<{
  (e: "update", value: string): void;
  (e: "update:priceSort", value: string): void;
}>();

const {t} = useI18n();

const isMaxStoneSize = computed({
  get: () => props.activePriceSort === 'max-stone-size',
  set: (val: boolean) => {
    emit('update:priceSort', val ? 'max-stone-size' : '');
  }
});

const isMinPrice = computed({
  get: () => props.activePriceSort === 'min-price',
  set: (val: boolean) => {
    emit('update:priceSort', val ? 'min-price' : '');
  }
});

const min = computed(() => props.filter.min ?? 0);
const max = computed(() => props.filter.max ?? 10000);


// Parse activeValue "min-max"
const parsedActiveValue = computed(() => {
  if (!props.activeValue) return {min: min.value, max: max.value};
  const parts = props.activeValue.split('-');
  if (parts.length !== 2) return {min: min.value, max: max.value};
  return {
    min: parseFloat(parts[0]),
    max: parseFloat(parts[1])
  };
});

const sliderRef = ref<HTMLElement | null>(null);
const isDragging = ref<'min' | 'max' | null>(null);

const localMin = ref(parsedActiveValue.value.min);
const localMax = ref(Math.max(parsedActiveValue.value.max, 300));

watch(() => props.activeValue, () => {
  if (!isDragging.value) {
    localMin.value = parsedActiveValue.value.min;
    localMax.value = Math.max(parsedActiveValue.value.max, 300);
  }
});

const currentMin = computed({
  get: () => localMin.value,
  set: (val) => {
    localMin.value = Math.min(val, localMax.value);
    emitUpdate();
  }
});

const currentMax = computed({
  get: () => localMax.value,
  set: (val) => {
    localMax.value = Math.max(val, localMin.value, 300);
    emitUpdate();
  }
});

const emitUpdate = () => {
  if (localMin.value === min.value && localMax.value === max.value) {
    emit("update", "");
  } else {
    emit("update", `${localMin.value}-${localMax.value}`);
  }
}

const getPercentage = (value: number) => {
  return ((value - min.value) / (max.value - min.value)) * 100;
};

const minPercent = computed(() => getPercentage(currentMin.value));
const maxPercent = computed(() => getPercentage(currentMax.value));

const handleMouseDown = (type: 'min' | 'max') => {
  isDragging.value = type;
};

const handleMouseMove = (e: MouseEvent | TouchEvent) => {
  if (!isDragging.value || !sliderRef.value) return;

  const rect = sliderRef.value.getBoundingClientRect();
  const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
  const offsetX = clientX - rect.left;
  const percentage = Math.max(0, Math.min(100, (offsetX / rect.width) * 100));
  let value = (percentage / 100) * (max.value - min.value) + min.value;

  // Snap to step or options
  value = selectOptions.value.reduce((prev, curr) => {
    return (Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev);
  });

  value = Number(value.toFixed(2));

  if (isDragging.value === 'min') {
    localMin.value = Math.min(value, localMax.value);
  } else {
    localMax.value = Math.max(value, localMin.value, 300);
  }
};

const handleMouseUp = () => {
  if (isDragging.value) {
    emitUpdate();
  }
  isDragging.value = null;
};

onMounted(() => {
  window.addEventListener('mousemove', handleMouseMove);
  window.addEventListener('mouseup', handleMouseUp);
  window.addEventListener('touchmove', handleMouseMove, {passive: false});
  window.addEventListener('touchend', handleMouseUp);
});

onUnmounted(() => {
  window.removeEventListener('mousemove', handleMouseMove);
  window.removeEventListener('mouseup', handleMouseUp);
  window.removeEventListener('touchmove', handleMouseMove);
  window.removeEventListener('touchend', handleMouseUp);
});

const selectOptions = computed(() => {
  const options = [];
  let cur = min.value;
  while (cur <= max.value) {
    options.push(Number(cur.toFixed(2)));
    if (cur < 2000) cur += 100;
    else if (cur < 10000) cur += 500;
    else if (cur < 100000) cur += 5000;
    else cur += 25000;
  }

  if (options[options.length - 1] !== max.value) {
    options.push(max.value);
  }
  return options;
});

const isMaxItemButtonAllowed = computed(() => {
  return [
    "engagement_ring",
    "engagement_ring_rings_of_the_stars",
  ].includes(props.catalogItemType)
})

const isMinPriceButtonAllowed = computed(() => {
  return props.catalogItemType.includes('wedding_ring')
})

</script>

<template>
  <div class="sidebar-price-range px-2 py-4">
    <div class="relative w-full h-1 bg-gray-200 rounded-full mb-8 mt-2" ref="sliderRef">
      <!-- Active range bar -->
      <div
          class="absolute h-full bg-brand-main rounded-full"
          :style="{ left: minPercent + '%', right: (100 - maxPercent) + '%' }"
      ></div>

      <!-- Min handle -->
      <div
          class="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 bg-brand-main border-2 border-brand-main rounded-full cursor-pointer z-10 touch-none shadow-sm hover:scale-110 transition-transform"
          :style="{ left: minPercent + '%' }"
          @mousedown="handleMouseDown('min')"
          @touchstart.prevent="handleMouseDown('min')"
      >
      </div>

      <!-- Max handle -->
      <div
          class="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 bg-brand-main border-2 border-brand-main rounded-full cursor-pointer z-10 touch-none shadow-sm hover:scale-110 transition-transform"
          :style="{ left: maxPercent + '%' }"
          @mousedown="handleMouseDown('max')"
          @touchstart.prevent="handleMouseDown('max')"
      >
      </div>
    </div>

    <!-- Select boxes -->
    <div class="flex justify-between gap-4">
      <div class="flex-1">
        <label class="block text-[10px] text-gray-400 uppercase tracking-wider mb-1">Von</label>
        <select
            v-model.number="currentMin"
            class="w-full border-b border-gray-200 py-1 text-sm outline-none focus:border-brand-main transition-colors appearance-none bg-transparent"
        >
          <option v-for="opt in selectOptions.filter(o => o <= currentMax)" :key="'min-'+opt" :value="opt">
            {{ opt === max ? '∞' : opt.toLocaleString('de-DE') }} €
          </option>
        </select>
      </div>
      <div class="flex-1 text-right">
        <label class="block text-[10px] text-gray-400 uppercase tracking-wider mb-1">Bis</label>
        <select
            v-model.number="currentMax"
            class="w-full border-b border-gray-200 py-1 text-sm outline-none focus:border-brand-main transition-colors appearance-none bg-transparent text-right"
        >
          <option v-for="opt in selectOptions.filter(o => o >= currentMin && o >= 300)" :key="'max-'+opt" :value="opt">
            {{ opt === max ? '∞' : opt.toLocaleString('de-DE') }} €
          </option>
        </select>
      </div>
    </div>

    <!-- Max Stone Size Checkbox -->
    <div v-if="isMaxItemButtonAllowed" class="pt-2 ">
      <label class="flex items-start gap-3 cursor-pointer group">
        <div class="relative flex items-center justify-center">
          <input
              type="checkbox"
              v-model="isMaxStoneSize"
              class="peer sr-only"
          >
          <div
              class="w-5 h-5 border-2 border-gray-200 rounded-md transition-all duration-200 peer-checked:border-brand-main peer-checked:bg-brand-main group-hover:border-gray-300"></div>
          <svg
              class="absolute w-3.5 h-3.5 text-white scale-0 transition-transform duration-200 peer-checked:scale-100"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="4"
              stroke-linecap="round"
              stroke-linejoin="round"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </div>
        <div class="flex flex-col">
          <span class="text-sm font-semibold text-gray-900 group-hover:text-brand-main transition-colors">
            {{ t('components.catalog.filter.price_sort.max-stone-size.label') }}
          </span>
          <span class="text-[11px] text-gray-500 leading-relaxed mt-0.5">
            {{ t('components.catalog.filter.price_sort.max-stone-size.description') }}
          </span>
        </div>
      </label>
    </div>

    <!-- Min Price (Best Price) Checkbox -->
    <div v-if="isMinPriceButtonAllowed" class="pt-2 ">
      <label class="flex items-start gap-3 cursor-pointer group">
        <div class="relative flex items-center justify-center">
          <input
              type="checkbox"
              v-model="isMinPrice"
              class="peer sr-only"
          >
          <div
              class="w-5 h-5 border-2 border-gray-200 rounded-md transition-all duration-200 peer-checked:border-brand-main peer-checked:bg-brand-main group-hover:border-gray-300"></div>
          <svg
              class="absolute w-3.5 h-3.5 text-white scale-0 transition-transform duration-200 peer-checked:scale-100"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="4"
              stroke-linecap="round"
              stroke-linejoin="round"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </div>
        <div class="flex flex-col">
          <span class="text-sm font-semibold text-gray-900 group-hover:text-brand-main transition-colors">
            {{ t('components.catalog.filter.price_sort.min-price.label') }}
          </span>
          <span class="text-[11px] text-gray-500 leading-relaxed mt-0.5">
            {{ t('components.catalog.filter.price_sort.min-price.description') }}
          </span>
        </div>
      </label>
    </div>

  </div>
</template>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
